import {useEffect, useRef, useState} from 'react';
import {Check, RotateCcw, ZoomIn, ZoomOut} from 'lucide-react';
import type {ImageCrop} from '../lib/types';
import {CROP_MAX_SCALE, CROP_MIN_SCALE, DEFAULT_CROP, clampCrop, cropTransform} from '../lib/crop';

/**
 * In-place, non-destructive image cropper. The image fills the frame (object-fit:
 * cover) so it is never stretched; the user pans by dragging and zooms with the
 * wheel / slider / buttons. Apply (Enter, the ✓ button, or clicking outside)
 * returns the crop; Escape cancels. Only the visible area changes — the source
 * image is untouched.
 */
export function PhotoCropper({
  src,
  frameW,
  frameH,
  zoom,
  initial,
  onApply,
  onCancel,
}: {
  src: string;
  frameW: number;
  frameH: number;
  /** Preview zoom factor, so screen-space drags map to frame pixels. */
  zoom: number;
  initial: ImageCrop;
  onApply: (crop: ImageCrop) => void;
  onCancel: () => void;
}) {
  const [crop, setCrop] = useState<ImageCrop>(() => clampCrop(initial, frameW, frameH));
  const cropRef = useRef(crop);
  useEffect(() => {
    cropRef.current = crop;
  }, [crop]);
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  const setClamped = (next: ImageCrop) => setCrop(clampCrop(next, frameW, frameH));
  const adjustScale = (delta: number) =>
    setClamped({...cropRef.current, scale: cropRef.current.scale + delta});

  const startPan = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const start = cropRef.current;
    const onMove = (me: MouseEvent) => {
      setClamped({
        ...start,
        offset_x: start.offset_x + (me.clientX - sx) / zoom,
        offset_y: start.offset_y + (me.clientY - sy) / zoom,
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Native non-passive wheel listener so we can preventDefault page scroll.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      adjustScale(e.deltaY < 0 ? 0.12 : -0.12);
    };
    el.addEventListener('wheel', onWheel, {passive: false});
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameW, frameH]);

  // Enter / Escape, and click-outside-to-apply. The outside listener is attached
  // on the next tick so the opening double-click doesn't immediately close it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onApply(cropRef.current);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onApply(cropRef.current);
    };
    window.addEventListener('keydown', onKey, true);
    const t = window.setTimeout(() => window.addEventListener('mousedown', onDocDown, true), 0);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onDocDown, true);
      window.clearTimeout(t);
    };
  }, [onApply, onCancel]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div
      ref={rootRef}
      style={{width: frameW, height: frameH}}
      className="relative"
      onMouseDown={stop}
      onClick={stop}
      onDoubleClick={stop}
    >
      {/* Crop frame — fixed size; image pans/zooms inside it */}
      <div
        ref={frameRef}
        className="absolute inset-0 overflow-hidden rounded bg-gray-900 ring-2 ring-indigo-500 cursor-move select-none"
        onMouseDown={startPan}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: cropTransform(crop),
            transformOrigin: 'center',
            pointerEvents: 'none',
          }}
        />
        {/* rule-of-thirds guides */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-y-0 left-1/3 w-px bg-white/30" />
          <div className="absolute inset-y-0 left-2/3 w-px bg-white/30" />
          <div className="absolute inset-x-0 top-1/3 h-px bg-white/30" />
          <div className="absolute inset-x-0 top-2/3 h-px bg-white/30" />
        </div>
      </div>

      {/* Toolbar */}
      <div
        className="absolute -top-10 left-0 flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-1 shadow-md"
        onMouseDown={stop}
        onClick={stop}
      >
        <button
          type="button"
          title="Zoom out"
          className="rounded p-1 hover:bg-accent disabled:opacity-40"
          disabled={crop.scale <= CROP_MIN_SCALE}
          onClick={() => adjustScale(-0.2)}
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <input
          type="range"
          min={CROP_MIN_SCALE}
          max={CROP_MAX_SCALE}
          step={0.05}
          value={crop.scale}
          onChange={(e) => setClamped({...crop, scale: Number(e.target.value)})}
          className="h-1 w-24 cursor-pointer accent-indigo-500"
        />
        <button
          type="button"
          title="Zoom in"
          className="rounded p-1 hover:bg-accent disabled:opacity-40"
          disabled={crop.scale >= CROP_MAX_SCALE}
          onClick={() => adjustScale(0.2)}
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Reset crop"
          className="rounded p-1 hover:bg-accent"
          onClick={() => setCrop(DEFAULT_CROP)}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
        <div className="mx-0.5 h-4 w-px bg-border" />
        <button
          type="button"
          title="Apply (Enter)"
          className="flex items-center gap-1 rounded bg-indigo-500 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-600"
          onClick={() => onApply(cropRef.current)}
        >
          <Check className="h-3 w-3" /> Apply
        </button>
      </div>
    </div>
  );
}
